import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { PlusIcon } from '@phosphor-icons/react/dist/csr/Plus';

import { PageActions } from '../../app/shell/PageActions';
import { ArrowClockwiseIcon } from '@phosphor-icons/react/dist/csr/ArrowClockwise';

import { DispensaryFormDialog } from './DispensaryFormDialog';
import { ReferenceLifecycleActions } from './ReferenceLifecycleActions';
import { Button } from '../../design-system/primitives/Button/Button';
import {
  ClientDataTable,
  type TableFilter,
} from '../../design-system/composites/DataTable/ClientDataTable';
import {
  type Column,
  type ListStatus,
  type Row,
} from '../../design-system/composites/DataTable/DataTable';
import { ApiError } from '../../lib/api/client';
import { dispensariesApi, type Dispensary } from '../../lib/api/reference';
import styles from './ReferenceList.module.css';

/**
 * The dispensary list — the root of the reference hierarchy.
 *
 * `dispensariesApi.list(true)` — ALWAYS `include_inactive=true`. This is an
 * ADMINISTRATOR screen (`ReferenceScreen`'s own gate — any authenticated
 * account can read, but only one holding `reference.manage` can act), and a
 * screen that hid a deactivated row would be how a retired dispensary gets
 * recreated as a duplicate the next time someone looks for it and does not
 * find it (`DispensaryController`'s own class docblock names this exact
 * failure). `renderStatus` below is what keeps a hidden row from also being
 * an UNMARKED one: an inactive row stays on this table and says so.
 *
 * Follows `UserListScreen.tsx` for the state machine and the create/edit
 * wiring: one `DataTable`, four list states, `onSaved`/`onChanged`/`onDeleted`
 * merge the row in place rather than triggering a refetch that would flash
 * the loading skeleton over every other row to reflect a change to one.
 */

export interface DispensaryListProps {
  permissions: readonly string[];
}

interface DispensaryRow extends Row, Dispensary {}

function toRow(node: Dispensary): DispensaryRow {
  return { ...node, id: node.uuid };
}

export function DispensaryList({ permissions }: DispensaryListProps) {
  const { t, i18n } = useTranslation();
  const canManage = permissions.includes('reference.manage');

  const [status, setStatus] = useState<ListStatus>('loading');
  const [rows, setRows] = useState<readonly DispensaryRow[]>([]);
  const [reference, setReference] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingNode, setEditingNode] = useState<Dispensary | undefined>(undefined);

  const load = useCallback(async () => {
    setStatus('loading');
    setReference(null);

    try {
      const list = await dispensariesApi.list(true);
      const mapped = list.map(toRow);
      setRows(mapped);
      setStatus(mapped.length === 0 ? 'empty' : 'ready');
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      setReference(error.requestId ?? null);
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  function nodeLabel(node: Dispensary): string {
    return i18n.language.startsWith('ar') ? node.name_ar : node.name_en;
  }

  function applyRowChange(uuid: string, changes: Partial<Dispensary>): void {
    setRows((current) => current.map((row) => (row.id === uuid ? { ...row, ...changes } : row)));
  }

  function applyRowDelete(uuid: string): void {
    setRows((current) => current.filter((row) => row.id !== uuid));
  }

  function openCreateForm(): void {
    setEditingNode(undefined);
    setFormOpen(true);
  }

  function openEditForm(node: Dispensary): void {
    setEditingNode(node);
    setFormOpen(true);
  }

  function closeForm(): void {
    setFormOpen(false);
  }

  function handleSaved(saved: Dispensary): void {
    setRows((current) => {
      const exists = current.some((row) => row.id === saved.uuid);
      return exists
        ? current.map((row) => (row.id === saved.uuid ? { ...row, ...saved } : row))
        : [...current, toRow(saved)];
    });
    if (status === 'empty') setStatus('ready');
    setFormOpen(false);
  }

  function renderName(row: Row): ReactNode {
    return nodeLabel(row as unknown as Dispensary);
  }

  function renderStatus(row: Row): ReactNode {
    // Property under test: an inactive row is MARKED, never merely present.
    return (row as unknown as Dispensary).is_active
      ? t('reference.statusActive')
      : t('reference.statusInactive');
  }

  function renderActions(row: Row): ReactNode {
    const node = row as unknown as Dispensary;
    return (
      <ReferenceLifecycleActions
        node={node}
        label={nodeLabel(node)}
        canManage={canManage}
        onEdit={openEditForm}
        onChanged={applyRowChange}
        onDeleted={applyRowDelete}
        activate={dispensariesApi.activate}
        deactivate={dispensariesApi.deactivate}
        remove={dispensariesApi.remove}
      />
    );
  }

  /**
   * The status facet. `is_active` is a BOOLEAN on the row and the Status
   * column renders it as a word, so neither the search box nor a sort on
   * that column could ever answer "show me the inactive ones" — the word
   * the reader would search for is not on the row at all.
   */
  const filters: readonly TableFilter[] = [
    {
      id: 'status',
      label: t('reference.columnStatus'),
      key: 'is_active',
      options: [
        { value: 'true', label: t('reference.statusActive') },
        { value: 'false', label: t('reference.statusInactive') },
      ],
    },
  ];

  const columns: readonly Column[] = [
    { key: 'name_en', label: t('reference.columnName'), render: renderName },
    { key: 'external_code', label: t('reference.columnCode'), neverMirrors: true },
    { key: 'status', label: t('reference.columnStatus'), render: renderStatus },
    { key: 'actions', label: t('reference.columnActions'), render: renderActions },
  ];

  return (
    <div className={styles['page']}>
      {/* No sub-title under the shell's <h1>; the tab's one command rides in
          the title row beside the level switch. */}
      {canManage ? (
        <PageActions>
          <Button onClick={openCreateForm} icon={<PlusIcon size={16} />}>
            {t('reference.addDispensary')}
          </Button>
        </PageActions>
      ) : null}

      <ClientDataTable
        caption={t('reference.tabDispensaries')}
        columns={columns}
        rows={rows}
        filters={filters}
        status={status}
        empty={{ body: t('reference.emptyDispensaries') }}
        error={{ title: t('reference.loadFailedDispensaries') }}
      />

      {status === 'error' ? (
        <div className={styles['recovery']}>
          <Button
            variant="secondary"
            onClick={() => {
              void load();
            }}
            icon={<ArrowClockwiseIcon size={16} />}
          >
            {t('reference.retry')}
          </Button>

          {reference === null ? null : (
            <p className={styles['reference']}>
              {t('auth.supportReference')}{' '}
              <bdi dir="ltr" data-testid="dispensaryListRequestId">
                {reference}
              </bdi>
            </p>
          )}
        </div>
      ) : null}

      <DispensaryFormDialog
        open={formOpen}
        node={editingNode}
        onClose={closeForm}
        onSaved={handleSaved}
      />
    </div>
  );
}
